import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, HelpCircle } from 'lucide-react';

const faqsData = [
  {
    question: 'What server virtualization platforms do you implement?',
    answer: 'We deploy and optimize industry-standard hypervisors, primarily VMware ESXi / vSphere and Microsoft Hyper-V. Our setups include configuring high-availability clusters, shared SAN/NAS storage volumes, live migrations, and automated backup policies.'
  },
  {
    question: 'How do you design and verify structured network cabling?',
    answer: 'Our certified technicians lay Cat6, Cat6A, and fiber optic cabling following strict IEEE standards. We manage structured terminations in racks, patch panel installations, and perform electronic testing with cable certifiers to guarantee speed and throughput.'
  },
  {
    question: 'Can you manage our migration to Microsoft 365 or cloud environments?',
    answer: 'Yes, we handle complete transitions to cloud workspaces. This includes migrating physical Exchange servers to Microsoft 365, setting up OneDrive/SharePoint collaborative storage, cloud backup setups, and managing IAM security policies on Microsoft Azure and AWS.'
  },
  {
    question: 'What features are included in your CCTV surveillance systems?',
    answer: 'We install high-definition IP cameras equipped with night vision, wide-dynamic range, and artificial intelligence (face/object recognition, line-crossing alarms). Feeds are compiled in local NVRs and configured for secure remote-feed monitoring on mobile devices.'
  },
  {
    question: 'Do you provide firewall installations and cybersecurity audits?',
    answer: 'We design and configure Next-Generation Firewalls (Sophos XGS, Fortinet FortiGate) to manage UTM, web filtering, and intrusion prevention. We also run vulnerability scans and endpoint protection agent deployments to safeguard corporate networks.'
  },
  {
    question: 'What is the process for leasing laptop and desktop hardware in bulk?',
    answer: 'First, we align on your computing specs (Processor, RAM, SSD) and OS image. We then compile the lease agreement (ranging from monthly to yearly packages). Systems are delivered pre-installed, and we supply swap replacements within hours if any unit malfunctions.'
  }
];

export default function FAQs() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faqs" className="relative py-24 border-t border-white/5 bg-navy-deep/40">

      {/* Background glow highlights */}
      <div className="absolute top-[20%] right-[-10%] w-[45vw] h-[45vw] rounded-full bg-brand-blue/3 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[20%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-brand-cyan/3 blur-[120px] pointer-events-none" />

      <div className="max-w-4xl mx-auto px-6 md:px-8">

        {/* Section Title */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-cyan/10 border border-brand-cyan/20 text-brand-cyan text-xs font-bold uppercase tracking-widest mb-6"
          >
            <HelpCircle size={14} />
            <span>Support Database</span>
          </motion.div>

          <h2 className="font-display font-black text-4xl md:text-5xl text-white tracking-tight mb-4">
            Frequently Asked <span className="text-gradient">Questions</span>
          </h2>

          <p className="font-sans text-slate-400">
            Find answers to common questions about our corporate configurations, rentals, and operations.
          </p>
        </div>

        {/* Accordion List */}
        <div className="flex flex-col gap-4">
          {faqsData.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className={`rounded-2xl border transition-all duration-300 overflow-hidden ${isOpen
                    ? 'bg-navy-dark border-brand-cyan/30 shadow-lg shadow-brand-cyan/5'
                    : 'glass-panel border-white/5 hover:border-white/15'
                  }`}
              >
                {/* Accordion Trigger */}
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 md:py-6 flex items-center justify-between gap-4 text-left transition-colors cursor-pointer"
                >
                  <span className="font-display font-bold text-sm md:text-base text-white tracking-wide hover:text-brand-cyan transition-colors">
                    {faq.question}
                  </span>

                  {/* Rotating Icon */}
                  <div className={`p-1.5 rounded-lg transition-colors ${isOpen ? 'bg-brand-cyan/10 text-brand-cyan' : 'bg-white/5 text-slate-400'
                    }`}>
                    {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                  </div>
                </button>

                {/* Animated Body */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      <div className="px-6 pb-6 md:pb-8 pt-1 border-t border-white/5">
                        <p className="font-sans text-xs md:text-sm text-slate-400 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
